import { useState, useEffect } from "react";
import {
    CheckCircle,
    Clock,
    Play,
    AlertCircle,
    ArrowRight,
    Loader2,
    Sparkles,
    Tag,
    BarChart3,
    MessageSquare,
    ShieldCheck,
    CalendarDays,
    TestTube2,
    Phone,
    Hash,
    Eye,
    Download,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { formatMYT } from "@util/date";
import type { CallSession, CallCategory, CALL_CATEGORIES } from "@type/agent";
import {
    formatDuration,
    getScoreColor,
    getScoreLabel,
    statusBadgeConfig,
    sentimentChipConfig,
    chipVariantStyles,
    chipDotColor,
} from "@util/agent-scoring";
import { Badge } from "@component/badge";
import { Button } from "@component/button";
import {
    Card,
    CardContent,
} from "@component/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@component/tabs";
import { analyzeCallSession, getSessionAudioUrl } from "@service/agent";
import { WaveformPlayer } from "@component/agent-calls/waveform-player";
import { useSnackbar } from "@hook/use-snackbar";

interface CallSessionDetailProps {
    session: CallSession;
    callCategories: typeof CALL_CATEGORIES;
    onSessionUpdate?: (session: CallSession) => void;
}

const priorityConfig: Record<string, { label: string; color: string; bg: string }> = {
    high: { label: "High", color: "text-red-700", bg: "bg-red-50" },
    medium: { label: "Medium", color: "text-amber-700", bg: "bg-amber-50" },
    low: { label: "Low", color: "text-green-700", bg: "bg-green-50" },
};


function TranscriptPanel({ session }: { session: CallSession }) {
    const snackbar = useSnackbar();
    const [audioUrl, setAudioUrl] = useState<string | null>(session.audioUrl || null);
    const [audioLoading, setAudioLoading] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    useEffect(() => {
        let blobUrl: string | null = null;
        if (!session.audioUrl && session.id) {
            setAudioLoading(true);
            getSessionAudioUrl(session.id)
                .then((url) => {
                    blobUrl = url;
                    if (url) setAudioUrl(url);
                })
                .finally(() => setAudioLoading(false));
        }
        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [session.id]);

    // Drop consecutive duplicates from the same speaker — the agent sometimes
    // emits the same TTS chunk multiple times back-to-back, which clutters
    // the transcript without adding any information.
    const dedupedTranscript = session.transcript.filter((entry, i, arr) => {
        if (i === 0) return true;
        const prev = arr[i - 1];
        return !(
            prev.speaker === entry.speaker &&
            (prev.content ?? "").trim() === (entry.content ?? "").trim()
        );
    });

    const handleCopyTranscript = async () => {
        const text = dedupedTranscript
            .map((e) => `[${formatDuration(e.timestamp)}] ${e.speaker === "agent" ? "Agent" : session.contactName || "Contact"}: ${e.content}`)
            .join("\n");
        try {
            await navigator.clipboard.writeText(text);
            snackbar.showSuccess("Transcript copied to clipboard.");
        } catch {
            snackbar.showError("Failed to copy transcript.");
        }
    };

    const handleDownloadTranscript = () => {
        const text = dedupedTranscript
            .map((e) => `[${formatDuration(e.timestamp)}] ${e.speaker === "agent" ? "Agent" : session.contactName || "Contact"}: ${e.content}`)
            .join("\n");
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transcript-${session.id.slice(0, 8)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-5">
                    {audioLoading ? (
                        <div className="flex items-center gap-3 py-1">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            <span className="text-sm text-gray-500">Loading audio...</span>
                        </div>
                    ) : audioUrl ? (
                        <WaveformPlayer src={audioUrl} duration={session.duration} />
                    ) : (
                        <div className="flex items-center gap-3 py-1 text-gray-400 dark:text-gray-500">
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                <Play className="w-4 h-4 text-gray-400 ml-0.5" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No recording available.</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">Audio recording was not captured for this call.</p>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            {dedupedTranscript.length > 0 ? (
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-5">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Transcript</h4>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCopyTranscript}
                                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Copy
                                </button>
                                <button
                                    onClick={handleDownloadTranscript}
                                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Download
                                </button>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {dedupedTranscript.map((entry, i) => {
                                const isAgent = entry.speaker === "agent";
                                const displayName = isAgent
                                    ? (session as unknown as Record<string, unknown>).agentName as string || "Agent"
                                    : session.contactName || "Contact";
                                return (
                                    <div key={entry.id || i} className="flex gap-3.5 py-3.5 first:pt-0 last:pb-0">
                                        <div className={cn(
                                            "w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold",
                                            isAgent
                                                ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                                : "bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                                        )}>
                                            {isAgent ? "AI" : displayName[0]?.toUpperCase() || "C"}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {displayName}
                                                </span>
                                                {entry.timestamp > 0 && (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                                                        {formatDuration(entry.timestamp)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                                                {entry.content}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <div className="mb-3 rounded-full bg-gray-100 dark:bg-gray-800 p-3">
                                <MessageSquare className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No transcript available.</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">The transcript will appear here once the call is processed.</p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function ScorecardPanel({ session }: { session: CallSession }) {
    const overallScore = session.score;
    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const scoreOffset = circumference * (1 - overallScore / 100);
    const scoreStrokeColor = overallScore >= 85 ? "#16a34a" : overallScore >= 70 ? "#d97706" : "#dc2626";

    if (session.scoreBreakdown.length === 0) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="mb-3 rounded-full bg-gray-100 dark:bg-gray-800 p-3">
                            <BarChart3 className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No scorecard data available.</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Scores are generated automatically after the AI evaluates this call. Check back shortly.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center gap-7">
                        <div className="relative w-[88px] h-[88px] flex-shrink-0">
                            <svg width="88" height="88" viewBox="0 0 88 88" className="-rotate-90">
                                <circle cx="44" cy="44" r={radius} fill="none" className="stroke-gray-100 dark:stroke-gray-800" strokeWidth="7" />
                                <circle
                                    cx="44" cy="44" r={radius}
                                    fill="none"
                                    stroke={scoreStrokeColor}
                                    strokeWidth="7"
                                    strokeLinecap="round"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={scoreOffset}
                                    className="transition-all duration-700 ease-out"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-2xl font-bold text-gray-900 dark:text-white leading-none tracking-tight">{overallScore}</span>
                                <span className="text-xs font-semibold text-gray-400 mt-0.5">/ 100</span>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Overall Performance</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                {getScoreLabel(overallScore)}. Review individual criteria below for detailed breakdown.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Evaluation Criteria</h4>
                        </div>
                        <span className="text-xs text-gray-400 font-medium">{session.scoreBreakdown.length} criteria evaluated</span>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {session.scoreBreakdown.map((item, index) => {
                            const pct = Math.round((item.score / item.maxScore) * 100);
                            const barColor = pct >= 85 ? "bg-green-500" : pct >= 70 ? "bg-amber-500" : "bg-red-500";
                            return (
                                <div key={index} className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">{item.criterion}</div>
                                        {item.notes && (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 leading-snug">{item.notes}</p>
                                        )}
                                    </div>
                                    <div className="w-36 flex-shrink-0">
                                        <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                            <div className={cn("h-full rounded-full transition-all duration-500", barColor)} style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                    <div className={cn("w-11 text-right text-sm font-bold font-mono flex-shrink-0", pct >= 85 ? "text-green-600" : pct >= 70 ? "text-amber-600" : "text-red-600")}>
                                        {pct}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function CallSessionDetail({
    session,
    callCategories,
    onSessionUpdate,
}: CallSessionDetailProps) {
    const snackbar = useSnackbar();
    const [, setAnalyzing] = useState(false);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    const statusConfig = statusBadgeConfig[session.status] || { label: session.status, variant: "default" as const };
    const categoryOptions = Object.entries(callCategories) as [
        CallCategory,
        { label: string; color: string }
    ][];
    const sentimentConfig = sentimentChipConfig[session.sentiment];


    const handleCategoryChange = (category: CallCategory) => {
        setCategoryOpen(false);
        onSessionUpdate?.({ ...session, category });
    };

    const categoryInfo = session.category
        ? callCategories[session.category]
        : null;

    const handleExportSession = () => {
        const data = JSON.stringify(session, null, 2);
        const blob = new Blob([data], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `call-${session.id.slice(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header Card */}
            <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="px-6 py-5">
                    <div className="flex items-start justify-between">
                        {/* Left: Contact info */}
                        <div className="flex items-start gap-4">
                            {/* Phone Avatar */}
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                                    {session.contactName || "Unknown Contact"}
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-medium tabular-nums">
                                    {session.contactPhone}
                                </p>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRawJson(true)}>
                                <Eye className="w-3.5 h-3.5 mr-1.5" />
                                View Raw
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleExportSession}>
                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                Export
                            </Button>
                        </div>
                    </div>

                    {/* Meta Row */}
                    <div className="flex items-center gap-5 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex-wrap">
                        {/* Date */}
                        <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                            <CalendarDays className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {formatMYT(session.startedAt, "D MMM YYYY, h:mm A")}
                            </span>
                        </div>

                        {/* Duration */}
                        <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {formatDuration(session.duration)} duration
                            </span>
                        </div>

                        {/* Call ID */}
                        <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                            <Hash className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium font-mono text-gray-400 dark:text-gray-500">
                                {session.id.slice(0, 8)}
                            </span>
                        </div>

                        {/* Divider */}
                        <div className="w-px h-4 bg-gray-200 dark:bg-gray-700" />

                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Status Badge */}
                            <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[statusConfig.variant])}>
                                <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[statusConfig.variant])} />
                                {statusConfig.label}
                            </span>

                            {/* Sentiment Badge */}
                            <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[sentimentConfig.variant])}>
                                <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[sentimentConfig.variant])} />
                                {sentimentConfig.label}
                            </span>

                            {/* Test Call Badge */}
                            {session.isTestCall && (
                                <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200">
                                    <TestTube2 className="w-3 h-3 mr-1" />
                                    Test Call
                                </Badge>
                            )}

                            {/* Category Badge / Selector */}
                            <div className="relative">
                                <button
                                    onClick={() => setCategoryOpen(!categoryOpen)}
                                    title="Tag this call with a category for filtering and reporting"
                                    className={cn(
                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-colors",
                                        categoryInfo
                                            ? categoryInfo.color
                                            : "text-gray-500 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                    )}
                                >
                                    <Tag className="w-3 h-3" />
                                    {categoryInfo ? categoryInfo.label : "Set Category"}
                                </button>
                                {categoryOpen && (
                                    <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 w-48">
                                        {categoryOptions.map(([value, config]) => (
                                            <button
                                                key={value}
                                                onClick={() => handleCategoryChange(value)}
                                                className={cn(
                                                    "w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors",
                                                    session.category === value ? "font-semibold" : ""
                                                )}
                                            >
                                                <span className={cn("inline-block px-1.5 py-0.5 rounded", config.color)}>
                                                    {config.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Score - anchored right */}
                        {session.score > 0 && (
                            <div className="ml-auto">
                                <span className={cn("text-xl font-bold font-mono", getScoreColor(session.score))}>
                                    {session.score}%
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Fail Reason Banner */}
                {session.status === "failed" && session.failReason && (
                    <div className="mx-6 mb-2 flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <div>
                            <span className="font-medium">Fail reason:</span> {session.failReason}
                            {session.failCode && (
                                <span className="ml-2 text-xs text-red-500 dark:text-red-500">({session.failCode})</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Tabs - anchored to bottom of header */}
                <Tabs defaultValue="summary" className="w-full">
                    <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 pl-6">
                        <TabsList className="bg-transparent h-auto p-0 space-x-0">
                            <TabsTrigger
                                value="summary"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-400 px-5 py-3 text-sm font-medium"
                            >
                                Call Summary
                            </TabsTrigger>
                            <TabsTrigger
                                value="transcript"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-400 px-5 py-3 text-sm font-medium"
                            >
                                Transcript & Recording
                            </TabsTrigger>
                            <TabsTrigger
                                value="scorecard"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 dark:data-[state=active]:text-white text-gray-400 px-5 py-3 text-sm font-medium"
                            >
                                Scorecard
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6 bg-gray-50/30 dark:bg-gray-950/50">
                        {/* Tab: Call Summary */}
                        <TabsContent value="summary" className="mt-0">
                            <div className="space-y-4">
                                {/* AI Summary */}
                                <Card className="border-l-[3px] border-l-blue-500">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">AI Summary</h3>
                                        </div>
                                        {session.summary ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                                {session.summary}
                                            </p>
                                        ) : (
                                            <div className="flex items-start gap-2 py-2 text-gray-400 dark:text-gray-500">
                                                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No summary yet.</p>
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Use the Analyze button to generate an AI summary for this call.</p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Key Findings + Risk Signals */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Key Findings */}
                                    <Card>
                                        <CardContent className="p-5">
                                            <div className="flex items-center gap-2 mb-3">
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Key Findings</h4>
                                            </div>
                                            {session.keyFindings.length > 0 ? (
                                                <div className="space-y-2.5">
                                                    {session.keyFindings.map((finding, i) => (
                                                        <div key={i} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                                            <span className="w-5 h-5 rounded-md flex-shrink-0 bg-green-50 dark:bg-green-900/30 flex items-center justify-center text-xs font-bold text-green-600 mt-0.5">
                                                                {i + 1}
                                                            </span>
                                                            {finding}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 py-2 text-gray-400 dark:text-gray-500">
                                                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                                    <p className="text-sm italic">No key findings identified yet.</p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Risk Signals */}
                                    <Card>
                                        <CardContent className="p-5">
                                            <div className="flex items-center gap-2 mb-3">
                                                <AlertCircle className="w-4 h-4 text-red-500" />
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Risk Signals</h4>
                                            </div>
                                            {session.riskSignals.length > 0 ? (
                                                <div className="space-y-2.5">
                                                    {session.riskSignals.map((risk, i) => (
                                                        <div key={i} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                                            <span className="w-5 h-5 rounded-md flex-shrink-0 bg-red-50 dark:bg-red-900/30 flex items-center justify-center text-xs font-bold text-red-600 mt-0.5">
                                                                {i + 1}
                                                            </span>
                                                            {risk}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 py-2 text-green-600 dark:text-green-500">
                                                    <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                                                    <p className="text-sm font-medium">No risk signals detected — this call looks good.</p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Next Actions */}
                                <Card>
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Sparkles className="w-4 h-4 text-blue-600" />
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Next Actions</h4>
                                        </div>
                                        {session.nextActions && session.nextActions.actions.length > 0 ? (
                                            <div className="space-y-2">
                                                {session.nextActions.actions.map((item, i) => {
                                                    const pConfig = priorityConfig[item.priority] || priorityConfig.low;
                                                    return (
                                                        <div
                                                            key={i}
                                                            className="flex items-center justify-between px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                                                        >
                                                            <div className="flex items-center gap-2.5">
                                                                <ArrowRight className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {item.action}
                                                                </span>
                                                            </div>
                                                            <span className={cn(
                                                                "text-xs font-semibold px-2 py-0.5 rounded",
                                                                pConfig.bg, pConfig.color
                                                            )}>
                                                                {pConfig.label}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3 py-3 px-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                                <Sparkles className="w-5 h-5 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">No next actions generated yet.</p>
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                        Click &quot;Analyze&quot; to generate AI-powered action recommendations.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* Tab: Transcript & Recording */}
                        <TabsContent value="transcript" className="mt-0">
                            <TranscriptPanel session={session} />
                        </TabsContent>

                        {/* Tab: Scorecard */}
                        <TabsContent value="scorecard" className="mt-0">
                            <ScorecardPanel session={session} />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>

            {/* View Raw Dialog */}
            <Dialog open={showRawJson} onOpenChange={setShowRawJson}>
                <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Raw Session Data
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto">
                        <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 whitespace-pre-wrap break-words">
                            {JSON.stringify(session, null, 2)}
                        </pre>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(JSON.stringify(session, null, 2));
                                    snackbar.showSuccess("Raw session data copied to clipboard.");
                                } catch {
                                    snackbar.showError("Failed to copy session data.");
                                }
                            }}
                        >
                            Copy
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportSession}>
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download JSON
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
